import { batch, useDispatch } from 'react-redux'
import { ReactQueryDevtools } from 'react-query-devtools'
import { LinearProgress } from 'beam/dist/Progress'
import { makeStyles } from 'beam/dist/styles'
import { useEffect, useState, Suspense, ReactElement, ReactNode, useMemo } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import 'typeface-roboto'
import {
  getPrerequisites,
  setMediaFormats,
  getLaunchDarklyConfig,
  getAppProperties,
  getUserLicenses,
  setLandingPage,
  getLandingPage, setRouteReady,
  setInitialRoute,
} from './app.actions'
import LegacyLogin from './LegacyLogin'
import { tryAuthenticate, logout } from './LegacyLogin/login.actions'
import { C1Navbar } from './NavBar'
import ResetPassword from './ResetPassword'
import { SysConfig } from './SysConfig'
import { useTip } from './pendoUtils'
import { AuthenticatedUserProvider } from '../Permissions'
import { determineFeatureFlagValueFunction, FeatureFlagsProvider } from '../FeatureFlags'
import Footer from '../Common/Footer'
import { LAUNCHDARKLY_IMPLEMENTATION, PROPERTY_FILE_IMPLEMENTATION } from './app.constants'
import { useGATracker } from '../Common/useGATracker'
import { LoginPage } from './Login'
import { completeAuthorization } from './Login/loginActions'
import PostAuthRoutes from './PostAuthRoutes'
import { FEATURE_FLAGS } from '../FeatureFlags/featureFlags.constants'
import { useAppSelector } from '../root.reducer'
import { User } from '../Permissions/AuthenticatedUser.context'
import { useInitializeLD } from '../FeatureFlags/useInitializeLDClient'
import { getAvailableFeatureFunction } from '../FeatureFlags/withFeatureFlags'


const GOOGLE_ANALYTICS_ID = 'UA-63543333-1'
// const GOOGLE_ANALYTICS_ID = 'UA-142223457-1' // Ask Mark about access to this or replace it with your own test ID

const GOOGLE_ANALYTICS_HOSTNAME_WHITELIST = ['calabriocloud.com']
// const GOOGLE_ANALYTICS_HOSTNAME_WHITELIST = []

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    minWidth: '1200px',
    backgroundColor: theme.palette.grey[200],
  },
}))

interface AppProps {
  children: ReactNode,
}

const DEFAULT_LANDINGPAGE_TIMEOUT = 1000

export function App({ children = null }: AppProps): ReactElement {
  const dispatch = useDispatch()
  const loading = useAppSelector(state => state.app.loading)
  const ready = useAppSelector(state => state.app.ready)
  const routeReady = useAppSelector(state => state.app.routeReady)
  const canViewNewLogin = useAppSelector(state => state.app.prerequisites?.frLoginEnabled ?? false)
  const showSysConfig = useAppSelector(state => state.app.showSysConfig)
  const user = useAppSelector(state => state.app.user)
  const theme = useAppSelector(state => state.app.theme)
  const themeError = useAppSelector(state => state.app.themeError)
  const launchDarklyProps = useAppSelector(state => state.app.launchDarklyConfig)
  const featureFlagClient = useInitializeLD(user)
  const classes = useStyles()
  const forgerockIdToken = useAppSelector(state => state.app.forgerockIdToken)
  const frLoginUrl = useAppSelector(state => state.app.frLoginUrl)
  const frOpUrl = useAppSelector(state => state.app.frOpUrl)
  const frRealm = useAppSelector(state => state.app.frRealm)
  const wasAuthFR = useAppSelector(state => state.app.wasAuthFR)
  const location = useLocation()
  const [showLayout, setShowLayout] = useState(true)
  const initialRoute = useAppSelector(state => state.app.initialRoute)

  const launchDarklyReady = useMemo(() => {
    return launchDarklyProps?.implementation === PROPERTY_FILE_IMPLEMENTATION
      || launchDarklyProps?.implementation === LAUNCHDARKLY_IMPLEMENTATION && featureFlagClient
  }, [launchDarklyProps, featureFlagClient])

  const onLoad = async () => {
    batch(() => {
      dispatch(setMediaFormats())
      dispatch(getPrerequisites())
      dispatch(tryAuthenticate(forgerockIdToken))
      dispatch(getLaunchDarklyConfig())
      dispatch(getAppProperties())
    })
  }
  useEffect(() => {
    onLoad()
  }, [])

  useEffect(() => {
    if (ready && user
      && launchDarklyReady) {
      batch(() => {
        if (shouldUseLandingPageService()) {
          dispatch(getLandingPage(getLandingPageServiceEndpoint(), getLandingPageTimeout()))
        } else {
          dispatch(setRouteReady(true))
        }
      })
    } else if (!user) {
      batch(() => {
        dispatch(setLandingPage(null))
        dispatch(setRouteReady(true))
      })
    }
  }, [user, launchDarklyReady, ready])

  const onUserChange = async () => {
    batch(() => {
      dispatch(setRouteReady(false))
      dispatch(getLaunchDarklyConfig())
      dispatch(getAppProperties())

      if (user)
        dispatch(getUserLicenses(user.id))
    })
  }
  useEffect(() => {
    onUserChange()
  }, [user])

  const onLogout = async () => {
    setShowLayout(false)
    await dispatch(logout(forgerockIdToken, frLoginUrl, frOpUrl, frRealm, wasAuthFR))
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    window.history.replaceState({}, null as $TSFIXME, window.location.origin)
    setShowLayout(true)
  }

  const onReAuth = async () => {
    setShowLayout(false)
    await dispatch(logout('reAuth', frLoginUrl, frOpUrl, frRealm, wasAuthFR))
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    window.history.replaceState({}, null as $TSFIXME, window.location.origin)
    setShowLayout(true)
  }

  useEffect(() => {
    if (location.pathname === '/logout') {
      onLogout()
    } else if (location.pathname === '/reAuth') {
      onReAuth()
    }
  }, [location, forgerockIdToken, frLoginUrl, frOpUrl, frRealm, wasAuthFR])

  useGATracker(GOOGLE_ANALYTICS_ID, GOOGLE_ANALYTICS_HOSTNAME_WHITELIST)
  useTip(user, theme, themeError)

  const shouldUseLandingPageService = () => {
    return getAvailableFeatureFunction({ launchDarklyProps, featureFlagClient })(FEATURE_FLAGS.USE_LANDINGPAGE_SERVICE)
  }

  const getLandingPageServiceEndpoint = () => {
    return determineFeatureFlagValueFunction({ user, launchDarklyProps, featureFlagClient }, FEATURE_FLAGS.LANDINGPAGE_SERVICE_PATH)
  }

  const getLandingPageTimeout = () => {
    let timeout = determineFeatureFlagValueFunction({ user, launchDarklyProps, featureFlagClient }, FEATURE_FLAGS.LANDINGPAGE_TIMEOUT)
    try {
      if (timeout === undefined) {
        timeout = DEFAULT_LANDINGPAGE_TIMEOUT
      } else if (typeof timeout === 'string') {
        timeout = parseInt(timeout)
      }
    } catch (e) {
      timeout = DEFAULT_LANDINGPAGE_TIMEOUT
    }

    return timeout
  }

  const shouldHideNav = () => {
    if (launchDarklyProps?.implementation === LAUNCHDARKLY_IMPLEMENTATION) {
      return (featureFlagClient && user)
        ? !!featureFlagClient.variation(FEATURE_FLAGS.FEDRAMP_LOGIN_BANNER, false) && !user.fedRAMPCompliance
        : false
    }

    return false
  }

  const getRoutes = (user: User | null) => {
    return (
      <Switch>
        <Route path="/resetPassword/:uuid" component={ResetPassword} />
        <Route
          render={() =>
            user
              ? routeReady && !initialRoute ? <PostAuthRoutes>{children}</PostAuthRoutes> : <LinearProgress color={'secondary'} />
              : canViewNewLogin
                ? <LoginPage onSuccessfulLogin={user => dispatch(completeAuthorization(user))} />
                : <LegacyLogin />
          }
        />
      </Switch>
    )
  }

  useEffect(() => {
    if (initialRoute) {
      dispatch(setInitialRoute(null))
    }
  }, [initialRoute])

  if (initialRoute) {
    return (
      // This assumes that the initialRoute is set to a valid route. The population of initialRoute should be done in a way
      // that ensures it is a valid route.
      <Redirect to={initialRoute} />
    )
  }

  return (
    <AuthenticatedUserProvider value={user}>
      {ready && <FeatureFlagsProvider value={{ user, launchDarklyProps, featureFlagClient }}>
        <div className={classes.container}>

          <C1Navbar c1user={user} hideNav={shouldHideNav()} />

          {loading && <LinearProgress color="secondary" />}
          <Suspense fallback={<LinearProgress color="secondary" />}>{ready && showLayout && (showSysConfig ? <SysConfig /> : getRoutes(user))}</Suspense>
          {user && <Footer mini /> /* Login page (!user) has its own footer */}
        </div>
        <ReactQueryDevtools initialIsOpen={false} />
      </FeatureFlagsProvider>}
    </AuthenticatedUserProvider>
  )
}
